import { apiFetchJson } from '@wundo/client/api.ts';
import type { SendableNotificationChannel } from '@wundo/shared/notificationChannel.ts';
import type {
  NotificationDeliveryContentResponse,
  NotificationDeliveryItem,
  NotificationHistoryGroup,
  NotificationHistoryKind,
  NotificationSendOrigin,
  NotificationSendSummary,
} from '@wundo/shared/notificationHistory.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';

/** A cohort is a few hundred people, so one page holds a whole press. */
export const DELIVERIES_PAGE_SIZE = 100;

/** What the history list narrows to; a field left out keeps every send. */
export interface NotificationSendFilters {
  group?: NotificationHistoryGroup;
  exclude_group?: NotificationHistoryGroup;
  kind?: string;
  channel?: SendableNotificationChannel;
  sent_by?: NotificationSendOrigin;
}

/** Null lists the sends of every organisation. */
export function listNotificationSends(
  organisationId: string | null,
  filters: NotificationSendFilters,
  page: number,
  limit: number,
): Promise<PaginatedResponse<NotificationSendSummary>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (organisationId) params.set('organisation_id', organisationId);
  for (const [key, value] of Object.entries(filters)) {
    if (value) params.set(key, value);
  }
  return apiFetchJson<PaginatedResponse<NotificationSendSummary>>(
    `/admin/notification-sends?${params.toString()}`,
  );
}

/** Every notification the history can hold, grouped, meetings first. */
export async function listNotificationSendKinds(): Promise<NotificationHistoryKind[]> {
  const { data } = await apiFetchJson<{ data: NotificationHistoryKind[] }>(
    '/admin/notification-sends/kinds',
  );
  return data;
}

export async function getNotificationSend(
  sendId: string,
): Promise<NotificationSendSummary> {
  const { data } = await apiFetchJson<{ data: NotificationSendSummary }>(
    `/admin/notification-sends/${encodeURIComponent(sendId)}`,
  );
  return data;
}

/** The people a send was meant for, the bounced and failed ones first. */
export function listNotificationDeliveries(
  sendId: string,
  page: number,
): Promise<PaginatedResponse<NotificationDeliveryItem>> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(DELIVERIES_PAGE_SIZE),
  });
  return apiFetchJson<PaginatedResponse<NotificationDeliveryItem>>(
    `/admin/notification-sends/${encodeURIComponent(sendId)}/deliveries?${params.toString()}`,
  );
}

/** What one person received; its sign-in and unsubscribe links are masked. */
export async function getNotificationDeliveryContent(
  sendId: string,
  deliveryId: string,
): Promise<NotificationDeliveryContentResponse> {
  const { data } = await apiFetchJson<{ data: NotificationDeliveryContentResponse }>(
    `/admin/notification-sends/${encodeURIComponent(sendId)}/deliveries/${encodeURIComponent(deliveryId)}`,
  );
  return data;
}
